import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient, axiosClient } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import { Button, Card, Input, Select, useToast } from '../../design-system';
import '../styles/ClientRequests.css';

// Ask a client for documents or information.
//
// The description matters more than usual here: it is what the client actually
// reads in their portal, so the field is prominent and the helper text says so.
function OrgAdminCreateClientRequest() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    clientId: searchParams.get('clientId') || '',
    title: '',
    description: '',
    priority: 'medium',
    dueDate: '',
    assignedUser: '',
  });

  useEffect(() => {
    apiClient.get('/api/clients/options')
      .then((res) => setClients(res.data))
      // The lean picker endpoint may not exist in every deployment; fall back
      // to the full list rather than rendering an empty selector.
      .catch(() => apiClient.get('/api/clients').then((r) => setClients(r.data)).catch(() => {}));
    axiosClient.get('/users').then((res) => setOrgUsers(res.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.clientId) return toast.danger('Pick a client');
    if (!form.title.trim()) return toast.danger('Say what you need');

    setSaving(true);
    try {
      const body = {
        clientId: form.clientId,
        title: form.title.trim(),
        description: form.description.trim(),
        priority: form.priority,
        dueDate: form.dueDate || undefined,
        assignedUsers: form.assignedUser ? [form.assignedUser] : [],
      };
      const res = await apiClient.post('/api/client-requests', body);
      toast.success('Request created — the client can see it in their portal');
      navigate(`/org-admin/client-requests/${res.data._id}`);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to create the request');
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  return (
    <OrgAdminLayout title="Request something from a client" subtitle="This appears in the client's portal">
      <Card padding="md">
        <form onSubmit={submit} className="cr-form">
          <Select
            label="Client"
            value={form.clientId}
            onChange={(e) => setForm({ ...form, clientId: e.target.value })}
          >
            <option value="">— pick a client —</option>
            {clients.map((c) => (
              <option key={c._id} value={c._id}>
                {c.displayName}{c.clientCode ? ` (${c.clientCode})` : ''}
              </option>
            ))}
          </Select>

          <Input
            label="What do you need?"
            placeholder="e.g. April–June bank statements"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            helperText="The client sees this as the request title."
          />

          <div className="ds-field">
            <label className="ds-field-label" htmlFor="cr-desc">Details for the client</label>
            <textarea
              id="cr-desc"
              className="ds-input"
              rows="4"
              placeholder="Which accounts, which period, any format requirements…"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <span className="ds-field-helper">
              This is what they read in the portal — be specific enough that they can act without asking.
            </span>
          </div>

          <div className="cr-form-row">
            <Select
              label="Priority"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </Select>

            <Input
              label="Needed by"
              type="date"
              value={form.dueDate}
              onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
            />

            <Select
              label="Owner"
              value={form.assignedUser}
              onChange={(e) => setForm({ ...form, assignedUser: e.target.value })}
            >
              <option value="">Unassigned</option>
              {orgUsers.map((u) => <option key={u._id} value={u._id}>{u.name}</option>)}
            </Select>
          </div>

          <div className="cr-form-actions">
            <Button variant="secondary" type="button" onClick={() => navigate('/org-admin/client-requests')}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={saving}>Send request</Button>
          </div>
        </form>
      </Card>
    </OrgAdminLayout>
  );
}

export default OrgAdminCreateClientRequest;
